/**
 * StickyBookBar Component
 * 
 * Project: SunMarbella Holiday Rental Website
 * Version: 1.0.0
 * Last Modified: 2025-03-20
 * 
 * Purpose: Reusable sticky booking bar component used in PropertyDetail page and ImageGalleryModal
 * 
 * Features:
 * - Responsive design with mobile and desktop layouts
 * - Safe-area-inset support for notched devices
 * - Two modes: 'gallery' (simple) and 'property' (full pricing)
 * - Animated entrance/exit with Framer Motion
 * - Consistent styling across all usage locations
 * 
 * Props:
 * - mode: 'gallery' | 'property' - Determines layout complexity
 * - isVisible: boolean - Controls component visibility
 * - price: number - Property price per night
 * - rating: number - Property rating
 * - reviewCount: number - Number of reviews
 * - totalPrice?: number - Total price for dates (property mode only)
 * - nights?: number - Number of nights (property mode only)
 * - onBookNow: () => void - Book now button handler
 * - children?: React.ReactNode - Additional content (for property mode mobile)
 * 
 * Dependencies:
 * - React 19.2.4 (Component, useState, useEffect)
 * - Framer Motion 12 (AnimatePresence, motion.div)
 * - FontAwesome 6.5 (FontAwesomeIcon, faStar)
 * 
 * Changelog:
 * - 1.0.0 (2025-03-20): Initial implementation
 */

import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ProIcon } from './ProIcon';

interface StickyBookBarProps {
  mode: 'gallery' | 'property';
  isVisible: boolean;
  price: number;
  rating: number;
  reviewCount: number;
  title?: string;
  location?: string;
  totalPrice?: number;
  nights?: number;
  onBookNow: () => void;
  children?: React.ReactNode;
}

export const StickyBookBar: React.FC<StickyBookBarProps> = ({
  mode,
  isVisible,
  price,
  rating,
  reviewCount,
  title,
  location,
  totalPrice,
  nights,
  onBookNow,
  children
}) => {
  // Calculate display values based on availability (works in both modes)
  const showTotalPrice = totalPrice && nights;
  const displayPrice = showTotalPrice ? totalPrice : price;
  const priceLabel = showTotalPrice ? `${nights} nights` : '/ night';
  const buttonText = showTotalPrice ? 'Book Now' : 'Get Price';

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          exit={{ y: 100 }}
          data-chat-blocker="sticky-book-bar"
          className={`
            fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 
            p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] 
            z-[9996] ui-shadow-bottom
            block
          `}
        >
          {/* Mobile Layout */}
          <div className="md:hidden">
            {children ? (
              // When children are provided, render them directly (PropertyDetail mobile)
              children
            ) : (
              // Gallery mode or Property mode without children - simple layout
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-black text-slate-900">€{displayPrice.toLocaleString()}</div>
                  <div className="text-sm text-slate-500">{priceLabel}</div>
                </div>
                <button
                  onClick={onBookNow}
                  className="ui-btn-primary px-6 py-3 rounded-xl text-sm"
                >
                  {buttonText}
                </button>
              </div>
            )}
          </div>

          {/* Desktop Layout - Progressive content hiding: name → price → location → reviews */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 hidden md:block">
            <div className="flex items-center justify-between">
              {/* Left side content */}
              <div className="flex items-center gap-4 min-w-0">
                {/* Property Name - hidden on smaller desktops (xl breakpoint) */}
                {title && (
                  <button 
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="hidden xl:flex items-center gap-2 min-w-0 text-left hover:text-brand-ocean transition-colors"
                  >
                    <ProIcon icon="house-building" family="light" className="w-4 h-4 text-brand-ocean" />
                    <div className="text-sm font-semibold text-slate-900 truncate">{title}</div>
                  </button>
                )}
                {title && <div className="hidden xl:block h-8 w-px bg-slate-200" />}
                
                {/* Price - Always visible */}
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-xl font-black text-slate-900">€{displayPrice.toLocaleString()}</span>
                    <span className="text-sm text-slate-500">{priceLabel}</span>
                  </div>
                </div>
                
                {/* Location - hidden on smaller screens (lg breakpoint) */}
                {location && (
                  <>
                    <div className="hidden lg:block h-8 w-px bg-slate-200" />
                    <button 
                      onClick={() => document.getElementById('location')?.scrollIntoView({ behavior: 'smooth' })}
                      className="hidden lg:flex items-center gap-2 min-w-0 text-left hover:text-brand-ocean transition-colors"
                    >
                      <ProIcon icon="location-dot" family="light" className="w-4 h-4 text-brand-ocean" />
                      <div className="text-sm text-slate-600 truncate">{location}</div>
                    </button>
                  </>
                )}
                
                {/* Reviews - progressive reduction then hidden (md breakpoint) */}
                <div className="hidden md:flex items-center gap-1.5 text-sm text-slate-600">
                  <div className="h-8 w-px bg-slate-200" />
                  <button 
                    onClick={() => document.getElementById('reviews')?.scrollIntoView({ behavior: 'smooth' })}
                    className="flex items-center gap-1.5 hover:text-brand-ocean transition-colors"
                  >
                    <ProIcon icon="star" family="solid" className="w-4 h-4 text-brand-sun" />
                    <span className="font-bold">{rating}</span>
                    <span className="text-slate-400">·</span>
                    <span className="hidden xl:inline">{reviewCount} reviews</span>
                    <span className="hidden lg:inline xl:hidden">{reviewCount}</span>
                  </button>
                </div>
              </div>
              
              {/* Book button - Always visible */}
              <button
                onClick={onBookNow}
                className="ui-btn-primary px-8 py-3 rounded-xl text-sm flex-shrink-0"
              >
                {buttonText}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
